import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import axios from "../../../api/axios";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Stack,
  Pagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Alert,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Person as EditAttendeeIcon,
  Delete as DeleteAttendeeIcon,
  AttachMoney as MakePaymentIcon,
  CheckCircle as ApproveIcon,
  Cancel as DeniedIcon,
  AssignmentInd as PrintBadgeIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  DriveFileRenameOutline as EditTeamNameIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  MoreVert as ColumnMenuIcon,
} from '@mui/icons-material';
import ApplyPaymentModal from '../pages/ApplyPaymentModal'
import { useSnackbar } from 'notistack';
import useData from '../../../Hooks/useData';
import { DateTime } from 'luxon';
import styles from './GetAttendees.module.css';
import { formatMoney } from '../../../utils/helpers';

// ─── Helpers ───────────────────────────────────────────────────────────────────
const fmt$ = (v) =>
  v != null && !isNaN(Number(v)) ? formatMoney(v) : '$0.00';

// Generic comparator used by the column sorter. Empty values always sort last.
const compareValues = (a, b, type) => {
  const aEmpty = a == null || a === '';
  const bEmpty = b == null || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (type === 'number')  return Number(a) - Number(b);
  if (type === 'boolean') return a === b ? 0 : (a ? -1 : 1);
  if (type === 'date') {
    const da = new Date(a).getTime(), db = new Date(b).getTime();
    return (isNaN(da) ? 0 : da) - (isNaN(db) ? 0 : db);
  }
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
};

const StatusChip = ({ status }) => (
  <Chip
    icon={status === 'Approved' ? <ApproveIcon /> : <DeniedIcon />}
    label={status || 'N/A'}
    color={status === 'Approved' ? 'success' : 'error'}
    size="small"
  />
);

const YesNoChip = ({ value }) => (
  <Chip
    icon={value ? <ApproveIcon /> : <DeniedIcon />}
    label={value ? 'Yes' : 'No'}
    color={value ? 'success' : 'error'}
    size="small"
    variant="outlined"
  />
);

// ─── Action buttons (shared between primary and sub rows) ─────────────────────
const AttendeeActions = ({
  attendee,
  requireApprovalByRegistrationName,
  isExtra = false,
  onEdit, onDelete, onPayment, onApprove, onDeny, onPrintBadge,
}) => {
  const regKey = String(attendee?.BPAttendeeType ?? '').trim().toLowerCase();
  const isPending = String(attendee?.BPAttendeeStatus ?? '').trim() === 'Pending';
  const needsApproval = !isExtra && Boolean(requireApprovalByRegistrationName?.[regKey]) && isPending;

  return (
    <Stack direction="row" spacing={0} alignItems="center">
      <Tooltip title="Edit Attendee" arrow placement="top">
        <IconButton size="small" className={styles.actionBtn} onClick={() => onEdit(attendee.BPAttendeeID)}>
          <EditAttendeeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete Attendee" arrow placement="top">
        <IconButton size="small" className={styles.actionBtn} onClick={() => onDelete(attendee.BPAttendeeID)}>
          <DeleteAttendeeIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Payment and badge only shown for primary attendees */}
      {!isExtra && (
        <>
      <Tooltip title="Make Payment" arrow placement="top">
        <IconButton size="small" className={styles.actionBtn} onClick={() => onPayment(attendee.BPAttendeeID)}>
          <MakePaymentIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {needsApproval && (
        <>
          <Tooltip title="Approve" arrow placement="top">
            <IconButton size="small" className={styles.actionBtnApprove} onClick={() => onApprove(attendee.BPAttendeeID)}>
              <ApproveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Deny" arrow placement="top">
            <IconButton size="small" className={styles.actionBtnDeny} onClick={() => onDeny(attendee.BPAttendeeID)}>
              <DeniedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
      <Tooltip title="Print Badge" arrow placement="top">
        <IconButton size="small" className={styles.actionBtn} onClick={() => onPrintBadge(attendee.BPAttendeeID)}>
          <PrintBadgeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
        </>
      )}
    </Stack>
  );
};

// ─── Sub-attendee row — aligned to the main grid columns ──────────────────────
// `balance` is now passed in explicitly (sourced from the attendee's order)
// rather than read off attendee.BPAttendeeBalance directly.
const SubAttendeeRow = ({ attendee, actionHandlers, isLastInGroup, dates, balance }) => (
  <tr className={`${styles.subRow}${isLastInGroup ? ` ${styles.subRowLast}` : ''}`}>
    <td className={styles.expandCell} />
    <td><AttendeeActions attendee={attendee} {...actionHandlers} isExtra={true} /></td>
    <td className={styles.subRowConnectorCell}>
      <span className={styles.subRowConnector} aria-hidden="true">↳</span>
    </td>
    <td>{attendee.BPAttendeeType || '—'}</td>
    <td>{attendee.BPAttendeeFN || '—'}</td>
    <td>{attendee.BPAttendeeLN || '—'}</td>
    <td>{attendee.BPAttendeeEmail || '—'}</td>
    <td>{dates}</td>
    <td><StatusChip status={attendee.BPAttendeeStatus} /></td>
    <td className={styles.numericCell}>
      <span className={styles.subBalance}>-</span>
    </td>
    <td>{attendee.BPAttendeeGroup || '—'}</td>
    <td>{attendee.phone || '—'}</td>
    <td>{attendee.BPAttendeeChurch || '—'}</td>
    <td>{attendee.genderDisplay || '—'}</td>
    <td><YesNoChip value={attendee.bgCheckValid} /></td>
    <td><YesNoChip value={attendee.passportComplete} /></td>
  </tr>
);

// ─── Column count (must match thead below) ────────────────────────────────────
const COL_COUNT = 16;

// ─── Main component ────────────────────────────────────────────────────────────
const GetAttendees = () => {
  const { eventid } = useParams();
  const { auth } = useData();
  const token = auth?.token;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [eventData, setEventData] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [requireApprovalByRegistrationName, setRequireApprovalByRegistrationName] = useState({});

  // Order balances, keyed by OrderID. Populated from a single batched
  // request for just the order IDs referenced by the current attendees,
  // rather than fetching every order individually.
  const [orderBalanceById, setOrderBalanceById] = useState({});
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);

  // Dialogs
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [isUserSearching, setIsUserSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isCreatingAttendee, setIsCreatingAttendee] = useState(false);

  //for payments dialog
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [paymentAttendee, setPaymentAttendee] = useState(null);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);

  const [isEditTeamDialogOpen, setIsEditTeamDialogOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [isGroupsLoading, setIsGroupsLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [teamNameInput, setTeamNameInput] = useState('');
  const [isSavingTeamName, setIsSavingTeamName] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({ open: false, attendee: null });
  const [isDeletingAttendee, setIsDeletingAttendee] = useState(false);
  const [statusConfirmDialog, setStatusConfirmDialog] = useState({ open: false, action: '', attendee: null });
  const [isUpdatingAttendeeStatus, setIsUpdatingAttendeeStatus] = useState(false);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [hasGroups, setHasGroups] = useState(false);
  const [sortModel, setSortModel] = useState({ field: null, dir: null });
  const [columnMenu, setColumnMenu] = useState({ anchorEl: null, field: null });

  // ─── Data fetching ──────────────────────────────────────────────────────────
  const fetchData = useCallback(async ({ silent = false } = {}) => {
    setIsLoading(true);
    try {
      const res = await axios.get(`/api/attendees/${eventid}`);
      setAllData(res.data);
      if (!silent) enqueueSnackbar('Attendees fetched', { variant: 'success' });
    } catch {
      setError(true);
      if (!silent) enqueueSnackbar('Attendees fetch failed', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [eventid, enqueueSnackbar]);

  useEffect(() => { fetchData(); }, [fetchData]);


useEffect(() => {
  if (!eventid) return;
  axios.get(`/api/groups/getgroup/${eventid}`)
    .then(r => {
      const data = Array.isArray(r.data) ? r.data : [r.data];
      setHasGroups(data.filter(Boolean).length > 0);
    })
    .catch(() => setHasGroups(false));
}, [eventid]);

  useEffect(() => {
    if (!eventid) return;
    let cancelled = false;
    axios.get(`/api/events/${eventid}`)
      .then(r => { if (!cancelled) setEventData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [eventid]);

  useEffect(() => {
    if (!eventid) return;
    let cancelled = false;
    axios.get(`/api/registrations/events/${eventid}`)
      .then(r => {
        if (cancelled) return;
        const map = {};
        (Array.isArray(r.data) ? r.data : []).forEach(reg => {
          const key = String(reg?.RegistrationName ?? '').trim().toLowerCase();
          if (key) map[key] = Boolean(reg?.RequireApproval);
        });
        setRequireApprovalByRegistrationName(map);
      })
      .catch(() => { if (!cancelled) setRequireApprovalByRegistrationName({}); });
    return () => { cancelled = true; };
  }, [eventid, enqueueSnackbar]);

  // ─── Order balances ─────────────────────────────────────────────────────────
  // Collect the unique BPAttendeeOrderID values off the current attendee list
  // and fetch just those orders in a single batched call, rather than one
  // request per attendee (and rather than one per group, since a team
  // registration can share a single order across several attendees).
  //
  // NOTE: adjust the endpoint path, request shape, and OrderID/OrderBalance
  // field names below to match your actual orders API.
  useEffect(() => {
    const orderIds = [...new Set(
      (allData || [])
        .map(a => a.BPAttendeeOrderID)
        .filter(id => id != null)
    )];

    if (orderIds.length === 0) {
      setOrderBalanceById({});
      return;
    }

    let cancelled = false;
    setIsOrdersLoading(true);
    axios.post('/api/orders/batch', { orderIds })
      .then(r => {
        if (cancelled) return;
        const map = {};
        (Array.isArray(r.data) ? r.data : []).forEach(o => {
          map[o.OrderID] = o.OrderBalance;
        });
        setOrderBalanceById(map);
      })
      .catch(() => {
        if (!cancelled) {
          setOrderBalanceById({});
          enqueueSnackbar('Failed to load order balances', { variant: 'error' });
        }
      })
      .finally(() => { if (!cancelled) setIsOrdersLoading(false); });

    return () => { cancelled = true; };
  }, [allData, enqueueSnackbar]);

  // Balance for a single attendee, sourced from their order. Falls back to
  // the attendee's own BPAttendeeBalance if there's no order id or the
  // order hasn't loaded yet, so the column doesn't just flash to $0.00.
  const getAttendeeBalance = useCallback((attendee) => {
    const oid = attendee?.BPAttendeeOrderID;
    if (oid != null && Object.prototype.hasOwnProperty.call(orderBalanceById, oid)) {
      //console.log('Order Balance:',orderBalanceById);
      return orderBalanceById[oid];
    }
    return attendee?.BPAttendeeBalance;
  }, [orderBalanceById]);

  useEffect(() => {
    if (!isAddDialogOpen) return;
    const t = setTimeout(() => setDebouncedUserSearch(userSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [userSearch, isAddDialogOpen]);

  useEffect(() => {
    if (!isAddDialogOpen) return;
    let cancelled = false;
    setIsUserSearching(true);
    axios.get('/api/users', { params: { page: 1, limit: 25, search: debouncedUserSearch } })
      .then(r => { if (!cancelled) setUserOptions(r.data?.users || []); })
      .catch(() => { if (!cancelled) { setUserOptions([]); enqueueSnackbar('User search failed', { variant: 'error' }); } })
      .finally(() => { if (!cancelled) setIsUserSearching(false); });
    return () => { cancelled = true; };
  }, [debouncedUserSearch, isAddDialogOpen, enqueueSnackbar]);

  useEffect(() => {
    if (!isEditTeamDialogOpen) return;
    let cancelled = false;
    setIsGroupsLoading(true);
     axios.get(`/api/groups/getgroup/${eventid}`)
      .then(r => { if (!cancelled) setGroups(Array.isArray(r.data) ? r.data : [r.data]); })
      .catch(e => { if (!cancelled) { setGroups([]); enqueueSnackbar(e?.response?.data?.error || 'Failed to load teams', { variant: 'error' }); } })
      .finally(() => { if (!cancelled) setIsGroupsLoading(false); });
    return () => { cancelled = true; };
  }, [isEditTeamDialogOpen, eventid, enqueueSnackbar]);

  // ─── Group rows by BPAttendeeUserID ────────────────────────────────────────
  const groupedRows = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    const matches = (r) => {
      const name = `${r.BPAttendeeFN || ''} ${r.BPAttendeeLN || ''}`.toLowerCase();
      return (
        name.includes(lower) ||
        r.BPAttendeeEmail?.toLowerCase().includes(lower) ||
        r.BPAttendeeGroup?.toLowerCase().includes(lower) ||
        r.BPAttendeeStatus?.toLowerCase().includes(lower) ||
        r.BPAttendeeType?.toLowerCase().includes(lower) ||
        r.BPAttendeeCity?.toLowerCase().includes(lower) ||
        r.phone?.toLowerCase().includes(lower) ||
        r.genderDisplay?.toLowerCase().includes(lower)
      );
    };

    const map = new Map();
    (allData || []).forEach(row => {
      const uid = row.BPAttendeeUserID ?? `unknown_${row.BPAttendeeID}`;
      if (!map.has(uid)) map.set(uid, []);
      map.get(uid).push(row);
    });

    const result = [];
    map.forEach((attendees, uid) => {
      if (searchTerm && !attendees.some(matches)) return;
      const primary = attendees[0];
      const extras = attendees.slice(1);

      // Total balance = sum of each DISTINCT order's balance across this
      // group. Attendees sharing an order (e.g. a team registration under
      // one order) only count that order's balance once. Attendees with
      // no order id fall back to their own BPAttendeeBalance.
      const seenOrders = new Set();
      const totalBalance = attendees.reduce((sum, a) => {
        const oid = a.BPAttendeeOrderID;
        if (oid != null) {
          if (seenOrders.has(oid)) return sum;
          seenOrders.add(oid);
          const bal = Object.prototype.hasOwnProperty.call(orderBalanceById, oid)
            ? orderBalanceById[oid]
            : a.BPAttendeeBalance;
          return sum + (isNaN(Number(bal)) ? 0 : Number(bal));
        }
        return sum + (isNaN(Number(a.BPAttendeeBalance)) ? 0 : Number(a.BPAttendeeBalance));
      }, 0);

      result.push({
        id: `group_${uid}`,
        primary,
        extras,
        hasExtras: extras.length > 0,
        count: attendees.length,
        totalBalance,
      });
    });
    return result;
  }, [allData, searchTerm, orderBalanceById]);

  // ─── Sorting (sorts whole groups by their primary attendee) ─────────────────
  // Each column maps to a value accessor + type so the comparator stays generic.
  // Sorting operates on the group level so sub-attendee rows stay attached to
  // their parent.
  const SORT_COLUMNS = useMemo(() => [
    { key: 'count',            label: 'Regs',          type: 'number',  accessor: g => g.count },
    { key: 'BPAttendeeType',   label: 'Reg. Type',     type: 'string',  accessor: g => g.primary?.BPAttendeeType },
    { key: 'BPAttendeeFN',     label: 'First Name',    type: 'string',  accessor: g => g.primary?.BPAttendeeFN },
    { key: 'BPAttendeeLN',     label: 'Last Name',     type: 'string',  accessor: g => g.primary?.BPAttendeeLN },
    { key: 'BPAttendeeEmail',  label: 'Email',         type: 'string',  accessor: g => g.primary?.BPAttendeeEmail },
    { key: 'dates',            label: 'Dates',         type: 'date',    accessor: g => g.primary?.BPAttendeeArrival || eventData?.BP_StartDate },
    { key: 'BPAttendeeStatus', label: 'Status',        type: 'string',  accessor: g => g.primary?.BPAttendeeStatus },
    { key: 'totalBalance',     label: 'Total Balance', type: 'number', numeric: true, accessor: g => g.totalBalance },
    { key: 'BPAttendeeGroup',  label: 'Team',          type: 'string',  accessor: g => g.primary?.BPAttendeeGroup },
    { key: 'phone',            label: 'Phone',         type: 'string',  accessor: g => g.primary?.phone },
    { key: 'BPAttendeeChurch', label: 'Church',        type: 'string',  accessor: g => g.primary?.BPAttendeeChurch },
    { key: 'genderDisplay',    label: 'Gender',        type: 'string',  accessor: g => g.primary?.genderDisplay },
    { key: 'bgCheckValid',     label: 'Bg Check',      type: 'boolean', accessor: g => g.primary?.bgCheckValid },
    { key: 'passportComplete', label: 'Passport',      type: 'boolean', accessor: g => g.primary?.passportComplete },
  ], [eventData]);

  // Click cycles asc → desc → unsorted, matching the events DataGrid.
  const handleSort = (field) =>
    setSortModel(prev => {
      if (prev.field !== field) return { field, dir: 'asc' };
      if (prev.dir === 'asc')  return { field, dir: 'desc' };
      return { field: null, dir: null };
    });

  // Column menu (the "3 dots" button shown on header hover, like the DataGrid).
  const openColumnMenu = (e, field) => {
    e.stopPropagation(); // don't trigger the header's sort toggle
    setColumnMenu({ anchorEl: e.currentTarget, field });
  };
  const closeColumnMenu = () => setColumnMenu({ anchorEl: null, field: null });
  const applyMenuSort = (dir) => {
    setSortModel({ field: dir ? columnMenu.field : null, dir });
    closeColumnMenu();
  };

  const sortedGroups = useMemo(() => {
    if (!sortModel.field || !sortModel.dir) return groupedRows;
    const col = SORT_COLUMNS.find(c => c.key === sortModel.field);
    if (!col) return groupedRows;
    const factor = sortModel.dir === 'asc' ? 1 : -1;
    return [...groupedRows].sort(
      (g1, g2) => factor * compareValues(col.accessor(g1), col.accessor(g2), col.type)
    );
  }, [groupedRows, sortModel, SORT_COLUMNS]);

  const totalCount = sortedGroups.length;
  const pageCount = Math.ceil(totalCount / paginationModel.pageSize) || 1;
  const startIndex = paginationModel.page * paginationModel.pageSize;
  const pageRows = sortedGroups.slice(startIndex, startIndex + paginationModel.pageSize);

  const toggleRow = (id) =>
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

    const closePayment = async () => {
  setIsPaymentDialogOpen(false);
  setPaymentAttendee(null);
  setPaymentOrder(null);

  /*
   * Reload attendees and their order balances
   * after the payment dialog closes.
   */
  await fetchData({ silent: true });
};

  // ─── Action handlers ────────────────────────────────────────────────────────
  const handleEdit     = (id) => navigate(`/admin/events/attendeeedit/${id}`, { state: { eventid } });
  const handleDelete   = (id) => setConfirmDialog({ open: true, attendee: allData.find(a => a.BPAttendeeID === id) || null });
 const handlePayment = async (attendeeId) => {
  const attendee =
    allData.find(
      a => a.BPAttendeeID === attendeeId
    ) || null;

  if (!attendee) {
    enqueueSnackbar(
      'Unable to find attendee.',
      { variant: 'error' }
    );
    return;
  }

  setPaymentAttendee(attendee);
  setPaymentOrder(null);
  setIsPaymentDialogOpen(true);
  setIsPaymentLoading(true);

  try {
    const orderId = attendee.BPAttendeeOrderID;

    if (!orderId) {
      throw new Error('No existing order found for this attendee.');
     //create order based on quantity/cost of registration type if order doesn't exist
      //retrieve the # of registrations for the selected row(based on userid) and the cost of the registration type
      //1)get all attendees
      //2)get associated registrations
      //3)calculate total cost
      //4)create order with total cost 
      //5)create order items with the registration type and quantity
      //6) set the order id on the attendee record
    } 

    /*
     * We already have the balance from the batch
     * endpoint. Use it immediately.
     */
    const knownBalance =
      Object.prototype.hasOwnProperty.call(
        orderBalanceById,
        orderId
      )
        ? orderBalanceById[orderId]
        : attendee.BPAttendeeBalance;

    /*
     * Use the existing order endpoint if available.
     * The modal can still display the known balance
     * if this request is unavailable.
     */
    try {
      const response = await axios.get(
        `/api/orders/${orderId}`
      );
      //console.log('Order Info:',response.data, orderId);
      setPaymentOrder(response.data.order);

    } catch {
      /*
       * We still have enough information to show
       * the payment modal.
       */
      setPaymentOrder({});
    }

  } catch (error) {

    console.error(
      'Unable to load payment information:',
      error
    );

    enqueueSnackbar(
      error?.message ||
      'Unable to load payment information.',
      {
        variant: 'error'
      }
    );

  } finally {

    setIsPaymentLoading(false);

  }
};
  const handlePrintBadge = (id) => {};

  const openStatus = (action, id) =>
    setStatusConfirmDialog({ open: true, action, attendee: allData.find(a => a.BPAttendeeID === id) || null });
  const closeStatus = () => { if (!isUpdatingAttendeeStatus) setStatusConfirmDialog({ open: false, action: '', attendee: null }); };
  const handleApprove = (id) => openStatus('approve', id);
  const handleDeny    = (id) => openStatus('deny', id);

  const handleConfirmStatus = async () => {
    const { action, attendee } = statusConfirmDialog;
    if (!attendee?.BPAttendeeID) return;
    setIsUpdatingAttendeeStatus(true);
    try {
      await axios.post(`/api/attendees/${attendee.BPAttendeeID}/${action}`);
      enqueueSnackbar(action === 'approve' ? 'Attendee approved' : 'Attendee denied', { variant: 'success' });
      setStatusConfirmDialog({ open: false, action: '', attendee: null });
      await fetchData({ silent: true });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.error || 'Failed', { variant: 'error' });
    } finally {
      setIsUpdatingAttendeeStatus(false);
    }
  };

  const closeConfirm = () => { if (!isDeletingAttendee) setConfirmDialog({ open: false, attendee: null }); };
  const handleConfirmDelete = async () => {
    if (!confirmDialog.attendee?.BPAttendeeID) return;
    setIsDeletingAttendee(true);
    try {
      await axios.delete(`/api/attendees/${confirmDialog.attendee.BPAttendeeID}`);
      enqueueSnackbar('Attendee deleted', { variant: 'success' });
      setConfirmDialog({ open: false, attendee: null });
      await fetchData({ silent: true });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.error || 'Failed to delete', { variant: 'error' });
    } finally {
      setIsDeletingAttendee(false);
    }
  };

  const openAdd  = () => { setSelectedUser(null); setUserSearch(''); setDebouncedUserSearch(''); setUserOptions([]); setIsAddDialogOpen(true); };
  const closeAdd = () => { if (!isCreatingAttendee) setIsAddDialogOpen(false); };
  const handleCreateAttendee = async () => {
    if (!selectedUser) return;
    setIsCreatingAttendee(true);
    try {
      const resp = await axios.post('/api/attendees', { BPEventID: Number(eventid), BPAttendeeUserID: selectedUser.UserID, BPAttendeeStatus: 'Pending' });
      const attendeeId = resp.data?.BPAttendeeID;
      if (!attendeeId) throw new Error('No ID returned');
      enqueueSnackbar('Attendee created', { variant: 'success' });
      setIsAddDialogOpen(false);
      navigate(`/admin/events/attendeeedit/${attendeeId}`, { state: { eventid } });
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.existingAttendeeId) {
        enqueueSnackbar('Already exists — opening record', { variant: 'warning' });
        setIsAddDialogOpen(false);
        navigate(`/admin/events/attendeeedit/${e.response.data.existingAttendeeId}`, { state: { eventid } });
        return;
      }
      enqueueSnackbar(e?.response?.data?.error || 'Failed to create attendee', { variant: 'error' });
    } finally {
      setIsCreatingAttendee(false);
    }
  };

  const openEditTeam  = () => { setSelectedGroup(null); setTeamNameInput(''); setIsEditTeamDialogOpen(true); };
  const closeEditTeam = () => { if (!isSavingTeamName) setIsEditTeamDialogOpen(false); };
  const getGroupLabel = (g) => String(g?.BPGroupName ?? '');
  const handleSaveTeamName = async () => {
    if (!selectedGroup?.BPGroupsID) return;
    const nextName = teamNameInput.trim();
    if (!nextName) return;
    setIsSavingTeamName(true);
    try {
      const resp = await axios.put(`/api/groups/${selectedGroup.BPGroupsID}`, { BPGroupName: nextName });
      enqueueSnackbar('Team name updated', { variant: 'success' });
      setGroups(prev => prev.map(g => g.BPGroupsID === selectedGroup.BPGroupsID ? { ...g, BPGroupName: resp.data?.BPGroupName ?? nextName } : g));
      setIsEditTeamDialogOpen(false);
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.error || 'Failed to update team name', { variant: 'error' });
    } finally {
      setIsSavingTeamName(false);
    }
  };

  const getUserLabel = (u) => {
    if (!u) return '';
    return (u.FullName || `${u.FirstName || ''} ${u.LastName || ''}`).trim() || u.UserName || String(u.UserID || '');
  };

  // ─── Export (exports allData flat, unchanged except balance now sourced
  //     from the attendee's order) ─────────────────────────────────────────
  const EXPORT_FIELDS = [
    { field: 'BPAttendeeType', label: 'Reg. Type' },
    { field: 'BPAttendeeFN',   label: 'First Name' },
    { field: 'BPAttendeeLN',   label: 'Last Name'  },
    { field: 'BPAttendeeEmail',label: 'Email'      },
    { field: 'BPAttendeeStatus',label:'Status'     },
    { field: 'BPAttendeeBalance',label:'Balance'   },
    { field: 'phone',           label: 'Phone'      },
    { field: 'BPAttendeeGroup', label: 'Team'      },
    { field: 'BPAttendeeDepartureDate', label: 'Departure' },
    { field: 'BPAttendeeArrivalDate',   label: 'Arrival'   },
    { field: 'BPAttendeeChurch', label: 'Church'   },
    { field: 'BPAttendeeCity',   label: 'City'     },
    { field: 'BPAttendeeState',  label: 'State'    },
    { field: 'BPAttendeeZip',    label: 'Zip'      },
    { field: 'BPAttendeeDOB',    label: 'DOB'      },
    { field: 'genderDisplay',    label: 'Gender'   },
    { field: 'bgCheckValid',     label: 'Bg Check' },
    { field: 'passportComplete', label: 'Passport' },
  ];
  const fmtExport = (field, v, row) => {
    if (field === 'bgCheckValid' || field === 'passportComplete') return v ? 'Yes' : 'No';
    // Balance column is exported from the order lookup, not the raw
    // attendee field, so it stays consistent with what's shown on screen.
    if (field === 'BPAttendeeBalance') {
      const n = Number(getAttendeeBalance(row));
      return isNaN(n) ? '' : formatMoney(n);
    }
    if (v == null) return '';
    if (['BPAttendeeDepartureDate','BPAttendeeArrivalDate','BPAttendeeDOB'].includes(field)) {
      const d = new Date(v); return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
    }
    return String(v);
  };
  const dlBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const csvEsc = (v) => { const s = String(v ?? ''); return /[",\n\r]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s; };
  const htmlEsc = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

  const handleExportCsv = () => {
    try {
      const header = EXPORT_FIELDS.map(f => csvEsc(f.label)).join(',');
      const lines  = allData.map(r => EXPORT_FIELDS.map(f => csvEsc(fmtExport(f.field, r[f.field], r))).join(','));
      dlBlob(new Blob([[header,...lines].join('\r\n')], {type:'text/csv;charset=utf-8'}), `attendees_${eventid}.csv`);
      enqueueSnackbar('Exported CSV', { variant: 'success' });
    } catch { enqueueSnackbar('Export failed', { variant: 'error' }); }
  };
  const handleExportExcel = () => {
    try {
      const thead = `<thead><tr>${EXPORT_FIELDS.map(f=>`<th>${htmlEsc(f.label)}</th>`).join('')}</tr></thead>`;
      const tbody = `<tbody>${allData.map(r=>`<tr>${EXPORT_FIELDS.map(f=>`<td>${htmlEsc(fmtExport(f.field,r[f.field],r))}</td>`).join('')}</tr>`).join('')}</tbody>`;
      dlBlob(new Blob([`<!DOCTYPE html><html><head><meta charset="UTF-8"/></head><body><table>${thead}${tbody}</table></body></html>`],{type:'application/vnd.ms-excel;charset=utf-8'}),`attendees_${eventid}.xls`);
      enqueueSnackbar('Exported Excel', { variant: 'success' });
    } catch { enqueueSnackbar('Export failed', { variant: 'error' }); }
  };

  const actionHandlers = {
    requireApprovalByRegistrationName,
    onEdit: handleEdit,
    onDelete: handleDelete, 
    onPayment: handlePayment,
    onApprove: handleApprove, 
    onDeny: handleDeny, 
    onPrintBadge: handlePrintBadge,
  };

  const secondaryHandlers = {
  requireApprovalByRegistrationName,
  onEdit: handleEdit, 
  onDelete: handleDelete, 
  onPayment: handlePayment,  // Add this
  onApprove: handleApprove, 
  onDeny: handleDeny,
  onPrintBadge: handlePrintBadge  // Add this
};


  const fmtDate = (v) => {
  if (!v) return null;
  const d = DateTime.fromISO(String(v), { zone: 'utc' });
  return d.isValid ? d.toFormat('MM/dd/yyyy') : String(v);
};

  // Attendee-specific dates, falling back to the event's start/end dates.
  const attendeeDates = (a) =>
    a?.BPAttendeeArrivalDate && a?.BPAttendeeDepartureDate
      ? `${fmtDate(a.BPAttendeeArrivalDate)} - ${fmtDate(a.BPAttendeeDepartureDate)}`
      : eventData?.BP_StartDate && eventData?.BP_EndDate
        ? `${fmtDate(eventData.BP_StartDate)} - ${fmtDate(eventData.BP_EndDate)}`
        : '—';


  const headerTitle = eventData?.BP_Title ? `View Attendees: ${eventData.BP_Title}` : 'View Attendees';

  const headerBanner = (
    <div className="full-row light-blue">
      <div className="container-fluid">
        <div className="row text-white pt-3 pb-3">
          <div className="col-md-12">
            <h1 className="text-uppercase">{headerTitle}</h1>
            <p>Manage attendee details</p>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) return (
    <main>{headerBanner}
      <div className="full-row white">
        <Box className={styles.loadingContainer}>
          <CircularProgress sx={{ color: '#003063' }} />
          <Typography className={styles.loadingText}>Loading attendees...</Typography>
        </Box>
      </div>
    </main>
  );

  if (error) return (
    <main>{headerBanner}
      <div className="full-row white">
        <Box className={styles.errorContainer}>
          <Typography color="error">Error fetching attendees. Please contact an admin.</Typography>
        </Box>
      </div>
    </main>
  );

  return (
    <main>
      {headerBanner}

      <div className="full-row white">
        <Box className={styles.container}>

          {/* ── Toolbar ── */}
          <Paper className={styles.searchPaper} elevation={0}>
            <Box className={styles.searchContainer}>
              <Box className={styles.buttonContainer}>
                <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/admin/events')}>
                  Back
                </Button>
                <Button variant="contained" startIcon={<AddIcon />} className={styles.addButton} onClick={openAdd}>
                  Add Attendee
                </Button>
                <Tooltip title={hasGroups ? '' : 'No teams yet — create a team first.'} placement="top" arrow>
                  <span>
                    <Button variant="outlined" startIcon={<EditTeamNameIcon />} className={styles.editTeamButton} onClick={openEditTeam} disabled={!hasGroups}>
                      Edit Team Name
                    </Button>
                  </span>
                </Tooltip>
                <Button variant="outlined" startIcon={<DownloadIcon />} className={styles.exportButton} onClick={handleExportCsv} disabled={!allData.length}>
                  Export CSV
                </Button>
                <Button variant="outlined" startIcon={<DownloadIcon />} className={styles.exportButton} onClick={handleExportExcel} disabled={!allData.length}>
                  Export Excel
                </Button>
              </Box>
              <TextField
                size="small"
                placeholder="Search by name, email, team, status, city, phone, gender…"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setPaginationModel(p => ({ ...p, page: 0 })); }}
                className={styles.searchField}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#003063', fontSize: 20 }} /></InputAdornment>,
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <ClearIcon className={styles.clearIcon} onClick={() => setSearchTerm('')} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Paper>

          {/* ── Accordion table ── */}
          <Box className={styles.tableWrapper}>
            <table className={styles.attendeeTable}>
              <thead>
                <tr>
                  {/* expand toggle */}
                  <th style={{ width: 40 }} />
                  <th>Actions</th>
                  {SORT_COLUMNS.map(col => {
                    const active = sortModel.field === col.key;
                    return (
                      <th
                        key={col.key}
                        className={`${styles.sortableHeader}${col.numeric ? ` ${styles.numericHeader}` : ''}${active ? ` ${styles.sortedHeader}` : ''}`}
                        onClick={() => handleSort(col.key)}
                      >
                        <span className={styles.headerLabel}>
                          {col.label}
                          <span className={styles.sortIconWrap}>
                            {active && sortModel.dir === 'asc' && <ArrowUpIcon className={styles.sortIcon} />}
                            {active && sortModel.dir === 'desc' && <ArrowDownIcon className={styles.sortIcon} />}
                            {!active && <ArrowUpIcon className={`${styles.sortIcon} ${styles.sortIconHover}`} />}
                          </span>
                          <IconButton
                            size="small"
                            className={styles.columnMenuBtn}
                            onClick={(e) => openColumnMenu(e, col.key)}
                            aria-label="Column menu"
                          >
                            <ColumnMenuIcon className={styles.columnMenuIcon} />
                          </IconButton>
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={COL_COUNT} style={{ textAlign: 'center', padding: '32px' }}>
                      <div style={{ fontWeight: 600, color: '#21252C' }}>No attendees found</div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '4px' }}>Try a different search term.</div>
                    </td>
                  </tr>
                )}
                {pageRows.map(({ id, primary, extras, hasExtras, count, totalBalance }) => {
                  const isOpen = expandedRows.has(id);
                  return (
                    <React.Fragment key={id}>
                      {/* ── Primary row ── */}
                      <tr
                        className={[
                          styles.primaryRow,
                          hasExtras ? styles.primaryRowHasExtras : '',
                          isOpen    ? styles.primaryRowExpanded  : '',
                        ].filter(Boolean).join(' ')}
                      >
                        {/* Expand toggle */}
                        <td className={styles.expandCell}>
                          {hasExtras && (
                            <Tooltip
                              title={isOpen ? 'Collapse' : `Show ${extras.length} more registration${extras.length !== 1 ? 's' : ''}`}
                              arrow placement="right"
                            >
                              <IconButton size="small" className={styles.expandButton} onClick={() => toggleRow(id)}>
                                {isOpen ? <ExpandIcon /> : <CollapseIcon />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </td>

                        {/* Actions */}
                         <td><AttendeeActions attendee={primary} {...secondaryHandlers} /></td> 

                        {/* Reg count badge */}
                        <td>
                          <Chip
                            label={count}
                            size="small"
                            className={count > 1 ? styles.countChipMulti : styles.countChipSingle}
                            variant={count > 1 ? 'filled' : 'outlined'}
                          />
                        </td>

                        <td>{primary.BPAttendeeType || '—'}</td>
                        <td>{primary.BPAttendeeFN || '—'}</td>
                        <td>{primary.BPAttendeeLN || '—'}</td>
                        <td>{primary.BPAttendeeEmail || '—'}</td>
                        <td>{attendeeDates(primary)}</td>
                        <td><StatusChip status={primary.BPAttendeeStatus} /></td>

                        {/* Total balance — rollup of each distinct order's balance in this group */}
                        <td className={styles.numericCell}>
                          <span className={styles.totalBalanceBadge}>
                            {fmt$(totalBalance)}
                            {hasExtras && (
                              <span className={styles.totalBalanceSub}>
                                ({count} reg{count !== 1 ? 's' : ''})
                              </span>
                            )}
                          </span>
                        </td>

                        <td>{primary.BPAttendeeGroup || '—'}</td>
                        <td>{primary.phone || '—'}</td>
                        <td>{primary.BPAttendeeChurch || '—'}</td>
                        <td>{primary.genderDisplay || '—'}</td>
                        <td><YesNoChip value={primary.bgCheckValid} /></td>
                        <td><YesNoChip value={primary.passportComplete} /></td>
                      </tr>

                      {/* ── Sub-attendee rows, aligned to the columns above ── */}
                      {hasExtras && isOpen && extras.map((a, i) => (
                        <SubAttendeeRow
                          key={a.BPAttendeeID}
                          attendee={a}
                          actionHandlers={actionHandlers}
                          isLastInGroup={i === extras.length - 1}
                          dates={attendeeDates(a)}
                          //balance={getAttendeeBalance(a)}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </Box>

          {/* ── Pagination ── */}
          <Paper className={styles.paginationPaper} elevation={0}>
            <Stack className={styles.paginationStack} direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Showing {totalCount > 0 ? startIndex + 1 : 0}–{Math.min(startIndex + paginationModel.pageSize, totalCount)} of {totalCount} users
              </Typography>
              <Pagination
                count={pageCount}
                page={paginationModel.page + 1}
                onChange={(_, p) => setPaginationModel(prev => ({ ...prev, page: p - 1 }))}
                color="primary" variant="outlined" shape="rounded" showFirstButton showLastButton
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={1}
                className="attendeesPagination"
              />
              <Box className={styles.pageSizeSelector}>
                <Typography variant="body2" color="text.secondary">Show:</Typography>
                <TextField
                  select size="small"
                  value={paginationModel.pageSize}
                  onChange={e => setPaginationModel({ page: 0, pageSize: parseInt(e.target.value) })}
                  SelectProps={{ native: true }}
                  className={`${styles.pageSizeField} pageSizeField`}
                >
                  {[10, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
                </TextField>
                <Typography variant="body2" color="text.secondary">per page</Typography>
              </Box>
            </Stack>
          </Paper>

          {/* ── Column menu (the "3 dots" sort menu, like the events DataGrid) ── */}
          <Menu
            anchorEl={columnMenu.anchorEl}
            open={Boolean(columnMenu.anchorEl)}
            onClose={closeColumnMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <MenuItem onClick={() => applyMenuSort('asc')}>
              <ListItemIcon><ArrowUpIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Sort by ASC</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => applyMenuSort('desc')}>
              <ListItemIcon><ArrowDownIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Sort by DESC</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => applyMenuSort(null)}
              disabled={columnMenu.field !== sortModel.field || !sortModel.dir}
            >
              <ListItemText inset>Unsort</ListItemText>
            </MenuItem>
          </Menu>

          {/* ── Edit Team Name ── */}
          <Dialog open={isEditTeamDialogOpen} onClose={closeEditTeam} maxWidth="sm" fullWidth>
            <DialogTitle className={styles.dialogTitle}>Edit Team Name</DialogTitle>
            <DialogContent>
              <Typography className={styles.dialogText}>Select a team and update its name.</Typography>
              <Autocomplete
                options={groups} value={selectedGroup}
                onChange={(_, v) => { setSelectedGroup(v); setTeamNameInput(v?.BPGroupName ?? ''); }}
                loading={isGroupsLoading} getOptionLabel={getGroupLabel}
                isOptionEqualToValue={(o, v) => o.BPGroupsID === v.BPGroupsID}
                renderOption={(props, opt) => (
                  <Box component="li" {...props} key={opt.BPGroupsID}>
                    <Typography variant="body2">{getGroupLabel(opt)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>Size: {opt?.BPGroupSize ?? 0}</Typography>
                  </Box>
                )}
                renderInput={p => (
                  <TextField {...p} label="Team" placeholder="Select a team…"
                    InputProps={{ ...p.InputProps, endAdornment: <>{isGroupsLoading && <CircularProgress size={18} />}{p.InputProps.endAdornment}</> }}
                  />
                )}
              />
              <TextField sx={{ mt: 2 }} label="New team name" size="small" fullWidth value={teamNameInput} onChange={e => setTeamNameInput(e.target.value)} disabled={!selectedGroup} />
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
              <Button onClick={closeEditTeam} disabled={isSavingTeamName} className={styles.cancelButton}>Cancel</Button>
              <Button onClick={handleSaveTeamName} disabled={!selectedGroup || !teamNameInput.trim() || isSavingTeamName} variant="contained" className={styles.confirmButton}
                startIcon={isSavingTeamName ? <CircularProgress size={16} color="inherit" /> : <EditTeamNameIcon />}>Save</Button>
            </DialogActions>
          </Dialog>

          {/* ── Add Attendee ── */}
          <Dialog open={isAddDialogOpen} onClose={closeAdd} maxWidth="sm" fullWidth>
            <DialogTitle className={styles.dialogTitle}>Add Attendee</DialogTitle>
            <DialogContent>
              <Typography className={styles.dialogText}>Search users and add them as an attendee.</Typography>
              <Autocomplete
                options={userOptions} value={selectedUser} onChange={(_, v) => setSelectedUser(v)}
                inputValue={userSearch} onInputChange={(_, v) => setUserSearch(v)}
                loading={isUserSearching} getOptionLabel={getUserLabel}
                isOptionEqualToValue={(o, v) => o.UserID === v.UserID}
                renderOption={(props, opt) => (
                  <Box component="li" {...props} key={opt.UserID}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">{getUserLabel(opt)}</Typography>
                      <Typography variant="caption" color="text.secondary">{opt.Email || opt.UserName || ''}</Typography>
                    </Box>
                  </Box>
                )}
                renderInput={p => (
                  <TextField {...p} label="Search users" placeholder="Name, email, username…"
                    InputProps={{ ...p.InputProps, endAdornment: <>{isUserSearching && <CircularProgress size={18} />}{p.InputProps.endAdornment}</> }}
                  />
                )}
              />
              {selectedUser && (
                <Box className={styles.userInfoBox} sx={{ mt: 2 }}>
                  <Typography className={styles.userInfoName}>{getUserLabel(selectedUser)}</Typography>
                  {selectedUser.Email && <Typography variant="body2" className={styles.userInfoEmail}>{selectedUser.Email}</Typography>}
                </Box>
              )}
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
              <Button onClick={closeAdd} disabled={isCreatingAttendee} className={styles.cancelButton}>Cancel</Button>
              <Button onClick={handleCreateAttendee} disabled={!selectedUser || isCreatingAttendee} variant="contained" className={styles.confirmButton}
                startIcon={isCreatingAttendee ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}>Add &amp; Edit</Button>
            </DialogActions>
          </Dialog>

          {/* ── Delete Attendee ── */}
          <Dialog open={confirmDialog.open} onClose={closeConfirm} maxWidth="sm" fullWidth>
            <DialogTitle className={styles.dialogTitle}>Delete Attendee</DialogTitle>
            <DialogContent>
              <Typography className={styles.dialogText}>Are you sure you want to permanently delete this attendee?</Typography>
              <Box className={styles.userInfoBox}>
                <Typography variant="body2" className={styles.userInfoName}>
                  <strong>Name:</strong> {`${confirmDialog.attendee?.BPAttendeeFN || ''} ${confirmDialog.attendee?.BPAttendeeLN || ''}`.trim() || 'N/A'}
                </Typography>
                <Typography variant="body2" className={styles.userInfoEmail}><strong>Email:</strong> {confirmDialog.attendee?.BPAttendeeEmail || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Team:</strong> {confirmDialog.attendee?.BPAttendeeGroup || 'N/A'}</Typography>
              </Box>
              <Alert severity="error"><strong>Warning:</strong> This action cannot be undone.</Alert>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
              <Button onClick={closeConfirm} variant="outlined" disabled={isDeletingAttendee} className={styles.cancelButton}>Cancel</Button>
              <Button onClick={handleConfirmDelete} variant="contained" color="error" disabled={isDeletingAttendee} className={styles.confirmButtonDelete}
                startIcon={isDeletingAttendee ? <CircularProgress size={16} color="inherit" /> : <DeleteAttendeeIcon />}>Delete Attendee</Button>
            </DialogActions>
          </Dialog>

          {/* ── Approve / Deny ── */}
          <Dialog open={statusConfirmDialog.open} onClose={closeStatus} maxWidth="sm" fullWidth>
            <DialogTitle className={styles.dialogTitle}>
              {statusConfirmDialog.action === 'approve' ? 'Approve Attendee' : 'Deny Attendee'}
            </DialogTitle>
            <DialogContent>
              <Typography className={styles.dialogText}>
                {statusConfirmDialog.action === 'approve'
                  ? 'Approve this attendee? A confirmation email will be sent.'
                  : 'Deny this attendee? A notification email will be sent.'}
              </Typography>
              <Box className={styles.userInfoBox}>
                <Typography variant="body2" className={styles.userInfoName}>
                  <strong>Name:</strong> {`${statusConfirmDialog.attendee?.BPAttendeeFN || ''} ${statusConfirmDialog.attendee?.BPAttendeeLN || ''}`.trim() || 'N/A'}
                </Typography>
                <Typography variant="body2"><strong>Team:</strong> {statusConfirmDialog.attendee?.BPAttendeeGroup || 'N/A'}</Typography>
              </Box>
              <Alert severity={statusConfirmDialog.action === 'approve' ? 'info' : 'warning'}>
                {statusConfirmDialog.action === 'approve'
                  ? 'Status will be set to Approved and an email will be sent.'
                  : 'Status will be set to Denied and an email will be sent.'}
              </Alert>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
              <Button onClick={closeStatus} variant="outlined" disabled={isUpdatingAttendeeStatus} className={styles.cancelButton}>Cancel</Button>
              <Button
                onClick={handleConfirmStatus}
                variant="contained"
                color={statusConfirmDialog.action === 'deny' ? 'error' : 'primary'}
                disabled={isUpdatingAttendeeStatus}
                className={statusConfirmDialog.action === 'deny' ? styles.confirmButtonDelete : styles.confirmButton}
                startIcon={isUpdatingAttendeeStatus ? <CircularProgress size={16} color="inherit" /> : statusConfirmDialog.action === 'approve' ? <ApproveIcon /> : <DeniedIcon />}
              >
                {statusConfirmDialog.action === 'approve' ? 'Approve & Send Email' : 'Deny & Send Email'}
              </Button>
            </DialogActions>
          </Dialog>

          <ApplyPaymentModal
        open={isPaymentDialogOpen}
        order={paymentOrder}
        onClose={closePayment}
        token={token}
      />

        </Box>
      </div>
    </main>
  );
};

export default GetAttendees;